/**
 * Vehicle type icons
 *
 * Two rows: a dropdown picks the vehicle type, and an icon row underneath picks the glyph the map
 * draws for it, drawn the way the icon library samples are — every entry as a button, the resolved
 * one outlined. Every pick saves the whole list straight away through the existing preference
 * helper (D8) and publishes it to `userPreferencesVar`, which is what repaints the markers without
 * a reload.
 */

import React, {useEffect, useRef, useState} from 'react';
import {useReactiveVar} from '@apollo/client';
import {langDirStyle, t} from '../../../common/translation';
import {userPreferencesVar} from '../../../common/user/settings/cache';
import {setUserPreference} from '../../../common/user/settings/set-preference';
import {ICON_LIBRARY} from '../../config/icon-library';
import {resolveVehicleIconEntry} from '../../config/svg-marker';
import IconPreview, {PREVIEW_ICON_FILL} from './icon-preview';

//The selected cell wears the same orange as the previews, in the style of the library samples.
const SELECTED_TINT = 'rgba(253, 126, 20, 0.12)';

const VEHICLE_TYPE_ICONS_KEY = 'vehicleTypeIcons';

/**
 * The vehicle types production carries. An id outside this list still resolves its marker through
 * the default chain in svg-marker.js; it simply has no row here to customize.
 */
export const VEHICLE_TYPES = [
    {typeID: '1', typeName: 'Truck'},
    {typeID: '2', typeName: 'Car'},
    {typeID: '3', typeName: 'Motorcycle'}
];

const isSameType = (savedEntry, typeID) => `${savedEntry?.vehicleTypeID}` === `${typeID}`;

const VehicleIconsModal = ({apolloClient, RAlert}) => {
    const userPreferencesState = useReactiveVar(userPreferencesVar);
    const [selectedTypeID, setSelectedTypeID] = useState(VEHICLE_TYPES[0].typeID);
    //a short-lived hint that the last click reached the backend; a SweetAlert toast would replace
    //the open modal, so the hint lives inside the dialog instead
    const [savedHint, setSavedHint] = useState(null);
    const hintTimer = useRef(null);

    const savedIcons = Array.isArray(userPreferencesState?.[VEHICLE_TYPE_ICONS_KEY])?
        userPreferencesState[VEHICLE_TYPE_ICONS_KEY]: [];
    //the same resolution the map uses, so the highlight sits on what the markers draw
    const currentEntry = resolveVehicleIconEntry(selectedTypeID);
    const isCustomized = savedIcons.some(savedEntry => isSameType(savedEntry, selectedTypeID));

    useEffect(() => () => clearTimeout(hintTimer.current), []);

    const flashHint = (outcome) => {
        setSavedHint(outcome);
        clearTimeout(hintTimer.current);
        hintTimer.current = setTimeout(() => setSavedHint(null), 2500);
    };

    /**
     * Save the list, then publish it locally so the map picks it up without waiting for the
     * preference refetch. Entries read back through Apollo carry `__typename`, which the input
     * type rejects, so only the pair itself ever goes over the wire (and into storage).
     */
    const save = async (nextList) => {
        const cleanList = nextList.map(entry => ({vehicleTypeID: entry.vehicleTypeID, iconID: entry.iconID}));
        const {error} = await setUserPreference(apolloClient, VEHICLE_TYPE_ICONS_KEY, cleanList);
        if(error)
        {
            flashHint('failed');
            return;
        }
        userPreferencesVar({...userPreferencesState, [VEHICLE_TYPE_ICONS_KEY]: cleanList});
        flashHint('saved');
    };

    /**
     * Pick an icon for the selected vehicle type, keeping every other type's entry. An entry the
     * type already has is replaced where it sits, so the saved list keeps its order.
     */
    const pickIcon = async (iconID) => {
        const nextList = savedIcons.some(savedEntry => isSameType(savedEntry, selectedTypeID))?
            savedIcons.map(savedEntry => isSameType(savedEntry, selectedTypeID)? {...savedEntry, iconID}: savedEntry):
            [...savedIcons, {vehicleTypeID: selectedTypeID, iconID}];
        await save(nextList);
    };

    /**
     * Back to the vehicle type's default: dropping its entry is what the resolution chain reads as
     * "no preference" (D4).
     */
    const resetType = async () => {
        await save(savedIcons.filter(savedEntry => !isSameType(savedEntry, selectedTypeID)));
    };

    return (
        <div className="card borderless shadow-none" style={langDirStyle()}>
            <div className="card-header">
                <div className="text-center p-2" style={{fontWeight: 'bolder'}}>
                    {t('Vehicle type icons')}
                </div>
            </div>
            <div className="card-body">
                <div className="row mb-3">
                    <label className="col-sm-3 col-form-label" htmlFor="vehicle-type-icons-type">
                        {t('Vehicle type')}
                    </label>
                    <div className="col-sm-9">
                        <select id="vehicle-type-icons-type" className="form-select" value={selectedTypeID}
                                onChange={event => setSelectedTypeID(event.target.value)}>
                            {
                                VEHICLE_TYPES.map(vehicleType => (
                                    <option key={vehicleType.typeID} value={vehicleType.typeID}>
                                        {t(vehicleType.typeName)}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                </div>
                <div className="row">
                    <label className="col-sm-3 col-form-label">{t('Icon')}</label>
                    <div className="col-sm-9">
                        <div className="d-flex flex-wrap">
                            {
                                ICON_LIBRARY.map(entry => {
                                    const isSelected = entry.id === currentEntry.id;
                                    return (
                                        <button type="button" key={entry.id} title={entry.label} aria-label={entry.label}
                                                aria-pressed={isSelected}
                                                className={`btn position-relative me-2 mb-2 p-1 border${isSelected? ' border-2 vehicle-icon-selected': ' bg-transparent'}`}
                                                style={isSelected? {borderColor: PREVIEW_ICON_FILL, backgroundColor: SELECTED_TINT}: undefined}
                                                onClick={async () => await pickIcon(entry.id)}>
                                            <IconPreview entry={entry}/>
                                            {
                                                isSelected &&
                                                <i className="bx bx-check position-absolute top-0 end-0 translate-middle text-white"
                                                   style={{
                                                       backgroundColor: PREVIEW_ICON_FILL,
                                                       borderRadius: '50%',
                                                       fontSize: '11px',
                                                       lineHeight: '14px',
                                                       width: '14px',
                                                       height: '14px',
                                                       textAlign: 'center'
                                                   }}/>
                                            }
                                        </button>
                                    );
                                })
                            }
                        </div>
                        <div className="d-flex align-items-center gap-2" style={{minHeight: '30px'}}>
                            {
                                isCustomized &&
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                        onClick={async () => await resetType()}>
                                    <i className="bx bx-undo"/>{t('Reset to default')}
                                </button>
                            }
                            {
                                savedHint === 'saved' &&
                                <span className="text-success small" role="status">
                                    <i className="bx bx-check"/>{t('Saved')}
                                </span>
                            }
                            {
                                savedHint === 'failed' &&
                                <span className="text-danger small" role="status">
                                    <i className="bx bx-x"/>{t('Save failed')}
                                </span>
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className="card-footer p-3">
                <div className="input-group justify-content-center">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => RAlert.close()}>
                        <i className="bx bx-window-close"/>{t('Close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VehicleIconsModal;
